<template>
  <v-container fluid>
    <v-speed-dial
      v-model="fab"
      bottom
      left
      fixed
      open-on-hover
      :direction="'top'"
      :transition="'slide-y-reverse-transition'"
    >
      <template v-slot:activator v-if="operation.id">
        <v-btn v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab">
            mdi-close
          </v-icon>
          <v-icon v-else>
            mdi-file-multiple
          </v-icon>
        </v-btn>
      </template>
      <v-tooltip left v-if="operation.clone_id || operation.is_draft">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="indigo"
            v-on="on"
            class="report-details-btn"
            @click="generateReport(operation.is_draft ? operation.id : operation.clone_id, true)"
          >
            <v-icon>mdi-clipboard-text</v-icon>
          </v-btn>
        </template>
        <span>PDA</span>
      </v-tooltip>
      <v-tooltip left v-if="!operation.is_draft && operation.id">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="green"
            v-on="on"
            class="report-details-btn"
            @click="generateReport(operation.id, false, true)"
          >
            <v-icon>mdi-ferry</v-icon>
          </v-btn>
        </template>
        <span>Vessel Report</span>
      </v-tooltip>
      <v-tooltip left v-if="!operation.is_draft && operation.id && isMediterraneanPrincipal">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="orange"
            v-on="on"
            class="report-details-btn"
            @click="generateReport(operation.id, false, false, true)"
          >
            <v-icon>mdi-file-document-outline</v-icon>
          </v-btn>
        </template>
        <span>MSC Report</span>
      </v-tooltip>
      <v-tooltip left v-if="!operation.is_draft && operation.id">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="indigo"
            v-on="on"
            class="report-details-btn"
            @click="generateReport(operation.id, false)"
          >
            <v-icon>mdi-clipboard-text</v-icon>
          </v-btn>
        </template>
        <span>{{ $store.state.auth.profile.role === 'accounts' ? 'FDA' : 'Job Report' }}</span>
      </v-tooltip>
      <!-- Download Consolidated PDF - Only show in Step 4 (Receipts) -->
      <v-tooltip left v-if="showConsolidatedPdf && hasAnyFiles">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="success"
            v-on="on"
            class="report-details-btn"
            :loading="consolidatedPdfLoading"
            :disabled="consolidatedPdfLoading"
            @click="getConsolidatedPdfForAllServices(operation.id)"
          >
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-btn>
        </template>
        <span>Download Consolidated PDF</span>
      </v-tooltip>
    </v-speed-dial>
    <v-overlay :value="overlayLoading" class="text-center">
      <div class="pb-4">
        Generating Report...
      </div>
      <v-progress-circular
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
  export default {
    name: 's-global-quote-details',
    props: {
      operation: {
        type: Object,
        default: {},
      },
      principalName: {
        type: String,
        default: '',
      },
      showConsolidatedPdf: {
        type: Boolean,
        default: false,
      },
      servicesOptedSorted: {
        type: Array,
        default: () => [],
      },
    },
    data(){
      return {
        fab: false,
        consolidatedPdfLoading: false,
        overlayLoading: false,
      };
    },
    computed: {
      hasAnyFiles() {
        return this.servicesOptedSorted.some(service => 
          service.file_urls && service.file_urls.length > 0
        );
      },
      isMediterraneanPrincipal() {
        const name = ((this.principalName || this.operation?.customer_name || this.operation?.card_name || '')).toString().toLowerCase();
        return name.includes('mediterranean shipping company');
      }
    },
    methods: {
      generateReport(operationId, isPda, vesselReport = false, mscReport = false) {
        this.overlayLoading = true;
        const method = mscReport ? 'generate_msc_report' : vesselReport ? 'generate_vessel_report' : 'generate_report';
        const args = method === 'generate_report' ? [operationId, isPda] : [operationId];
        this.$api.operations[method](...args)
          .then(response => {
            const url = window.URL.createObjectURL(response.data)

            const link = document.createElement('a')
            link.href = url
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()
          }).catch(err => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          }).finally(() => {
            this.overlayLoading = false;
          });
      },
      getConsolidatedPdfForAllServices(operationId) {
        this.overlayLoading = true;
        this.consolidatedPdfLoading = true;

        // Get all service IDs that have files
        const serviceIds = this.servicesOptedSorted
          .filter(service => service.file_urls && service.file_urls.length > 0)
          .map(service => service.id);

        if (serviceIds.length === 0) {
          this.$store.dispatch('setAlert', {
            status: 'warning',
            show: true,
            message: 'No files found to consolidate',
          });
          this.consolidatedPdfLoading = false;
          this.overlayLoading = false;
          return;
        }

        this.$api.operation_billed_items.consolidated_report_all_services(operationId)
          .then(response => {
            var b = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(b)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()

            this.$store.dispatch('setAlert', {
              status: 'success',
              show: true,
              message: 'Consolidated PDF downloaded successfully!',
            });
          }).catch(err => {
            if (err.response && err.response.data instanceof Blob) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result);
                  this.$store.dispatch('setAlert', {
                    status: 'error',
                    show: true,
                    message: json.error,
                  });
                } catch (parseErr) {
                  console.error("Non-JSON blob:", reader.result);
                }
              };
              reader.readAsText(err.response.data);
            }
          }).finally(() => {
            this.consolidatedPdfLoading = false;
            this.overlayLoading = false;
          });
      }
    }
  };
</script>